import { config } from "@/services/configService";

type ResolveOptions = {
  host?: string | null;
  uploads?: string | null;
  tenantId?: string | null;
  uploadsTenantId?: string | null;
};

const ABSOLUTE_PROTOCOL_REGEX = /^[a-z][a-z\d+\-.]*:/i;

export function isAbsoluteUrl(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  if (ABSOLUTE_PROTOCOL_REGEX.test(trimmed)) {
    return true;
  }

  if (trimmed.startsWith("//")) {
    return true;
  }

  if (trimmed.startsWith("/")) {
    return true;
  }

  return false;
}

function normalizeHost(hostCandidate: string | null | undefined): string {
  if (!hostCandidate) {
    return "";
  }

  let value = hostCandidate.trim();
  if (!value || value.startsWith("data:")) {
    return "";
  }

  if (value.startsWith("//")) {
    value = `https:${value}`;
  } else if (!/^https?:\/\//i.test(value)) {
    value = `https://${value.replace(/^\/+/, "")}`;
  }

  return value.replace(/\/+$/, "");
}

function trimLeadingSlash(value: string): string {
  return value.replace(/^\/+/, "");
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

function extractUploadsParts(uploadsCandidate: string | null | undefined) {
  if (!uploadsCandidate) {
    return { host: "", path: "" };
  }

  const value = uploadsCandidate.trim();
  if (!value) {
    return { host: "", path: "" };
  }

  if (!isAbsoluteUrl(value) || value.startsWith("/")) {
    return { host: "", path: value };
  }

  let parsed: URL | null = null;
  try {
    parsed = new URL(value.startsWith("//") ? `https:${value}` : value);
  } catch (_error) {
    return { host: "", path: value };
  }

  return {
    host: parsed.origin,
    path: parsed.pathname,
  };
}

function normalizeTenantSlug(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return "";
  }

  const slug = trimmed.includes("_") ? trimmed.split("_")[0] : trimmed;
  return slug.trim();
}

function replaceTenantSegment(path: string, tenantSlug: string): string {
  if (!tenantSlug) {
    return path;
  }

  const hasLeadingSlash = path.startsWith("/");
  const segments = path.split("/").filter((segment) => segment.length > 0);

  const uploadsIndex = segments.lastIndexOf("uploads");
  if (uploadsIndex !== -1) {
    if (uploadsIndex + 1 < segments.length) {
      segments[uploadsIndex + 1] = tenantSlug;
    } else {
      segments.push(tenantSlug);
    }
  } else if (segments.length) {
    segments[segments.length - 1] = tenantSlug;
  } else {
    segments.push("public", tenantSlug);
  }

  const joined = segments.join("/");
  const prefixed = hasLeadingSlash ? `/${joined}` : joined;
  return prefixed.endsWith("/") ? prefixed : `${prefixed}/`;
}

export function resolveMediaUrl(
  asset: unknown,
  options: ResolveOptions = {},
): string {
  if (asset === null || asset === undefined) {
    return "";
  }

  const raw = String(asset).trim();
  if (!raw) {
    return "";
  }

  if (isAbsoluteUrl(raw)) {
    return raw;
  }

  const uploadsCandidate = options.uploads ?? config.uploads ?? "";
  const tenantSlug = normalizeTenantSlug(
    options.uploadsTenantId ?? options.tenantId,
  );
  const uploadsParts = extractUploadsParts(uploadsCandidate);

  if (tenantSlug) {
    const basePath =
      uploadsParts.path && uploadsParts.path.trim()
        ? uploadsParts.path
        : uploadsCandidate;
    uploadsParts.path = replaceTenantSegment(
      basePath || config.uploads || "",
      tenantSlug,
    );
  }

  const hostCandidate = options.host ?? uploadsParts.host ?? config.host ?? "";
  const normalizedHost = normalizeHost(hostCandidate || uploadsParts.host);

  const uploadsSegment = uploadsParts.path
    ? trimLeadingSlash(trimTrailingSlash(uploadsParts.path))
    : "";
  const assetSegment = trimLeadingSlash(raw);

  const combinedPath = [uploadsSegment, assetSegment].filter(Boolean).join("/");
  const relativePath = `/${combinedPath}`.replace(/\/+$/g, "/");

  if (!normalizedHost) {
    return relativePath;
  }

  try {
    return new URL(relativePath, normalizedHost).toString();
  } catch (_error) {
    return `${normalizedHost}${relativePath}`;
  }
}

export function resolvePriceLabel(
  localized: unknown,
  fallback?: unknown,
): string {
  const maybeString = (value: unknown) => {
    if (value === null || value === undefined) {
      return "";
    }
    const str = String(value).trim();
    return str;
  };

  const maybeNumber = (value: unknown) => {
    if (value === null || value === undefined) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatAmount = (amount: number, currency?: string | null) => {
    const normalizedCurrency = currency || "EUR";
    try {
      return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: normalizedCurrency,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (_error) {
      return `${amount.toFixed(2)} ${normalizedCurrency}`;
    }
  };

  const priceObject =
    localized && typeof localized === "object"
      ? (localized as Record<string, unknown>)
      : null;

  const fallbackString = maybeString(fallback);
  if (fallbackString) {
    return fallbackString;
  }

  const grossString = priceObject
    ? maybeString(priceObject.grossAmountStr ?? priceObject.GrossAmountStr)
    : "";
  if (grossString) {
    const firstChar = grossString[0];
    const lastChar = grossString[grossString.length - 1];
    if (firstChar === lastChar && /[^\w\d]/.test(firstChar)) {
      return grossString.slice(0, -1).trim();
    }
    return grossString;
  }

  const grossNumeric = priceObject
    ? maybeNumber(priceObject.grossAmount ?? priceObject.GrossAmount)
    : null;
  if (grossNumeric !== null) {
    const currency =
      priceObject?.currencyCode ??
      priceObject?.CurrencyCode ??
      priceObject?.currency ??
      "EUR";
    return formatAmount(
      grossNumeric,
      typeof currency === "string" ? currency : "EUR",
    );
  }

  const fallbackGrossNumeric =
    fallback && typeof fallback === "object"
      ? maybeNumber(
          (fallback as Record<string, unknown>).grossAmount ??
            (fallback as Record<string, unknown>).GrossAmount,
        )
      : null;

  if (fallbackGrossNumeric !== null) {
    const fallbackCurrency =
      fallback && typeof fallback === "object"
        ? (fallback as Record<string, unknown>).currencyCode ??
          (fallback as Record<string, unknown>).CurrencyCode ??
          "EUR"
        : "EUR";
    return formatAmount(
      fallbackGrossNumeric,
      typeof fallbackCurrency === "string" ? fallbackCurrency : "EUR",
    );
  }

  return "";
}
